/**
 * The people who act for one client.
 *
 * Joint clients, corporate clients with several authorised contacts, executors,
 * interpreters and representatives are the norm rather than the exception (CR-21), so
 * this is a list of people and not a pair of fields on the client.
 *
 * Three things the API decides and this screen only reflects:
 *
 * 1. `is_primary` IS A POSITION, NOT A FLAG. There is exactly one, enforced by a
 *    partial unique index, and promoting somebody demotes the incumbent in the same
 *    transaction. So there is a "Make primary" command and no primary checkbox on the
 *    edit form — a checkbox would imply two could be ticked.
 *
 * 2. `has_portal_access` IS DERIVED from the contact's live matter grants, not stored.
 *    It answers "does this person hold at least one grant", never "what can they see".
 *    What they can see is the portal-access section, matter by matter.
 *
 * 3. REMOVAL IS SOFT AND TAKES THE PORTAL GRANTS WITH IT, in that order. A removed
 *    contact who kept a live grant would still be reading the client's file, so the
 *    confirmation says what will happen rather than asking "are you sure".
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  type ClientContactRecord,
  type ClientDetail,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  EmptyState,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  Modal,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';

import { RELATIONSHIP_OPTIONS, relationshipLabel } from './contactOptions';

const schema = z.object({
  full_name: z.string().min(1, 'Enter the contact’s name'),
  relationship: z.string(),
  email: z.string().email('Enter a valid email address').or(z.literal('')),
  phone: z.string(),
});

type Values = z.infer<typeof schema>;

export function ClientContactsTab({ client }: { client: ClientDetail }) {
  const [editing, setEditing] = useState<ClientContactRecord | null>(null);
  const [adding, setAdding] = useState(false);
  const [promoting, setPromoting] = useState<ClientContactRecord | null>(null);
  const [removing, setRemoving] = useState<ClientContactRecord | null>(null);

  return (
    <>
      <Card>
        <CardHeader
          title="Contacts"
          description="Everyone entitled to act for this client. The primary contact is who the firm writes to."
          actions={
            <Can actions={client._actions} action="edit">
              <Button variant="secondary" onClick={() => setAdding(true)}>
                Add contact
              </Button>
            </Can>
          }
        />
        <CardBody>
          {client.contacts.length === 0 ? (
            <EmptyState
              title="No contacts on this client"
              description="Nobody is recorded as able to act for them yet."
            />
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {client.contacts.map((contact) => (
                <li
                  key={contact.id}
                  className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-medium text-ink">{contact.full_name}</span>
                      {contact.is_primary && (
                        <Badge tone="primary" size="sm">
                          Primary contact
                        </Badge>
                      )}
                      <Badge tone="neutral" size="sm">
                        {relationshipLabel(contact.relationship)}
                      </Badge>
                      {/* Stated in words as well as colour: whether somebody outside
                          the firm can read the file is not a distinction to carry in a
                          hue alone (CR-25). */}
                      <Badge tone={contact.has_portal_access ? 'info' : 'neutral'} size="sm">
                        {contact.has_portal_access ? 'Portal access on' : 'No portal access'}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-ink-muted">
                      {contact.email || 'No email address'}
                      {contact.phone ? ` · ${contact.phone}` : ''}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Can actions={client._actions} action="edit">
                      <>
                        {!contact.is_primary && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPromoting(contact)}
                          >
                            Make primary
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setEditing(contact)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRemoving(contact)}>
                          Remove
                        </Button>
                      </>
                    </Can>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <ContactDialog
        open={adding}
        onClose={() => setAdding(false)}
        client={client}
        contact={null}
      />
      <ContactDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        client={client}
        contact={editing}
      />
      <PromoteDialog
        client={client}
        contact={promoting}
        onClose={() => setPromoting(null)}
      />
      <RemoveDialog client={client} contact={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

/** Add and edit are the same fields, so they are the same dialog. */
function ContactDialog({
  open,
  onClose,
  client,
  contact,
}: {
  open: boolean;
  onClose: () => void;
  client: ClientDetail;
  contact: ClientContactRecord | null;
}) {
  const api = useApi();
  const { toast } = useToast();

  const form = useZodForm(schema, {
    values: {
      full_name: contact?.full_name ?? '',
      relationship: contact?.relationship ?? 'self',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
    },
  });

  const save = useApiMutation<ClientContactRecord, Values>(
    {
      mutationFn: (values) => {
        // An absent value, never an empty string. "" records that this person HAS no
        // email address, which is what stops them being invited to the portal later.
        const body = {
          full_name: values.full_name,
          relationship: values.relationship,
          email: values.email || undefined,
          phone: values.phone || undefined,
        };
        return contact
          ? api.patch<ClientContactRecord>(
              `${STAFF.clientContact(client.id, contact.id)}/`,
              body,
            )
          : api.post<ClientContactRecord>(`${STAFF.clientContacts(client.id)}/`, body);
      },
      invalidates: [qk.all('clients')],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: contact ? 'Contact updated' : 'Contact added',
          description: saved.full_name,
          tone: 'success',
        });
        onClose();
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contact ? 'Edit contact' : 'Add contact'}
      description={
        contact
          ? undefined
          : 'Adding somebody here does not give them portal access. That is granted matter by matter.'
      }
    >
      <Form
        form={form}
        ariaLabel={contact ? 'Edit contact' : 'Add contact'}
        error={save.error}
        onSubmit={(values) => save.mutateAsync(values)}
      >
        <FormInput form={form} name="full_name" label="Name" autoFocus autoComplete="off" />
        <FormSelect
          form={form}
          name="relationship"
          label="Relationship to the client"
          options={RELATIONSHIP_OPTIONS}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <FormInput
            form={form}
            name="email"
            label="Email"
            type="email"
            hint="Required before a portal invitation can be sent."
          />
          <FormInput form={form} name="phone" label="Telephone" type="tel" />
        </div>
        <FormActions
          submitLabel={contact ? 'Save contact' : 'Add contact'}
          busy={save.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}

function PromoteDialog({
  client,
  contact,
  onClose,
}: {
  client: ClientDetail;
  contact: ClientContactRecord | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const incumbent = client.contacts.find((row) => row.is_primary);

  const promote = useApiMutation<ClientContactRecord, void>(
    {
      mutationFn: () =>
        api.post<ClientContactRecord>(
          `${STAFF.clientContactPrimary(client.id, contact?.id ?? '')}/`,
        ),
      invalidates: [qk.all('clients')],
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Primary contact changed',
          description: `${updated.full_name} is now the primary contact.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The change was refused',
          // A 409's `detail` is written for the reader and is actionable. Shown as
          // given rather than paraphrased into "something went wrong".
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The primary contact could not be changed. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <ConfirmModal
      open={contact !== null}
      onClose={onClose}
      title="Make this the primary contact"
      description={
        contact
          ? `${contact.full_name} will become the primary contact for ${client.display_name}.`
          : ''
      }
      consequences={
        incumbent
          ? `${incumbent.full_name} stops being the primary contact. There is only ever one.`
          : 'Nobody is currently designated, so nothing is demoted.'
      }
      confirmLabel="Make primary"
      loading={promote.isPending}
      onConfirm={() => promote.mutate()}
    />
  );
}

function RemoveDialog({
  client,
  contact,
  onClose,
}: {
  client: ClientDetail;
  contact: ClientContactRecord | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const remove = useApiMutation<void, void>(
    {
      mutationFn: () => api.delete<void>(STAFF.clientContact(client.id, contact?.id ?? '')),
      invalidates: [qk.all('clients')],
    },
    {
      onSuccess: () => {
        toast({
          title: 'Contact removed',
          description: 'Their portal access has been revoked across every matter.',
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The removal was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The contact could not be removed. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <ConfirmModal
      open={contact !== null}
      onClose={onClose}
      title="Remove this contact"
      description={
        contact ? `${contact.full_name} will be taken off ${client.display_name}’s file.` : ''
      }
      consequences={
        contact?.has_portal_access
          ? 'Every portal grant this person holds is revoked at the same time, so they lose sight of the file on their next request. The record of what they could see, and when, is kept.'
          : 'The record is kept rather than deleted — the firm’s retention obligation outlives the relationship.'
      }
      confirmLabel="Remove contact"
      destructive
      loading={remove.isPending}
      onConfirm={() => remove.mutate()}
    />
  );
}
